import { useState, useEffect } from 'react'
import { SetValueConfig } from 'react-hook-form'

import { IResponseTypes, IGenericResponseGST } from 'services/core/genericEntityGST'
import { RequestGST, RequestREAD } from 'services/Instances'
import { useModal, useLoading, useException } from 'hooks'
import { DEFAULT, FUNCTIONS } from 'utils'

export type IHandleChangePage = (_newPage: number) => void
export type IHandleChangeRowsPerPage = (_event: React.ChangeEvent<HTMLInputElement>) => void

type TypeApiName = 'Gst' | 'Read'

interface IUseGetProps<P> {
  params: P
  api: TypeApiName
  defaultErrorMessage?: string
  isPaginated?: boolean
  url: string
  formActions?: {
    setValue?: (_name: string, _value: unknown, _config?: SetValueConfig) => void
  }
  condition?: boolean
  formPersist?: {
    getValues: any
  }
}

interface IUseGetReturn<P, R> {
  data: R
  error: IGenericResponseGST
  refresh: (_params?: P) => void
  isFirstGet: boolean
  page: number
  rowsPerPage: number
  handleChangePage: (_newPage: number) => void
  handleChangeRowsPerPage: (_event: React.ChangeEvent<HTMLInputElement>) => void
  tableFooterControl: {
    typeFooter: 'online' | 'offline',
    page: number,
    rowsPerPage: number,
    handleChangePage: (_newPage: number) => void,
    handleChangeRowsPerPage: (_event: React.ChangeEvent<HTMLInputElement>) => void,
  }
}

const useGet = <P, R>(props?: IUseGetProps<P>): IUseGetReturn<P, R> => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { getException, createCustomException } = useException()

  const [data, setData] = useState<R>()
  const [error, setError] = useState<IGenericResponseGST>(null)
  const [isFirstGet, setIsFirstGet] = useState<boolean>(true)
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT.table.page_size)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const fillScreenData = (): void => {
    if (data['entity']) {
      Object.keys(data['entity']).forEach(formFieldName => {
        const value = data['entity'][formFieldName]

        props.formActions.setValue(formFieldName, value != null ? String(value) : '')
      })

      return
    }

    Object.keys(data).forEach(formFieldName => {
      const value = data[formFieldName]

      props.formActions.setValue(formFieldName, value != null ? String(value) : '')
    })
  }

  const getApi = (apiName: TypeApiName) => ({
    Gst: RequestGST,
    Read: RequestREAD
  })[apiName]

  const getPaginatedData = (params: P = props.params): void => {
    setLoading('loading')

    if (props.formPersist?.getValues) {
      params = { ...params, ...FUNCTIONS.filterObject(props.formPersist?.getValues(), Boolean) }
    }

    getApi(props.api)
      .get(props.url, {
        params: {
          ...params,
          pageSize: rowsPerPage,
          ...(params ? {
            pageNumber: params['pageNumber'] || page
          } : { pageNumber: page })
        }
      })
      .then(response => {
        if (response.status !== 200) {
          const { tipo, mensagem, detalhe } = response.data

          createCustomException({
            message: mensagem || props.defaultErrorMessage || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo],
            detalhe
          })
          setError({ mensagem, tipo, detalhe })
        }

        if (params['pageNumber']) {
          setPage(params['pageNumber'])
        }
        setData(response.data)
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          type: error.tipo || 'error',
          message: error.message,
          hideButtonCancel: true,
          txtButtonConfirm: 'OK',
        })
      })
      .finally(() => {
        if (isFirstGet) {
          setIsFirstGet(false)
        }
      })
  }

  const getData = (params: P = props.params): void => {
    setLoading('loading')

    getApi(props.api)
      .get(props.url, { params })
      .then(response => {
        if (response.status !== 200) {
          const { tipo, mensagem, detalhe } = response.data

          createCustomException({
            message: mensagem || props.defaultErrorMessage || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo],
            detalhe
          })
          setError({ mensagem, tipo, detalhe })
        }

        setData(response.data)
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          type: error.tipo || 'error',
          message: error.message,
          hideButtonCancel: true,
          txtButtonConfirm: 'OK',
        })
      })
      .finally(() => {
        if (isFirstGet) {
          setIsFirstGet(false)
        }
      })
  }

  useEffect(() => {
    if (Object.prototype.hasOwnProperty.call(props, 'condition')) {
      if (props.isPaginated && props.condition) {
        getPaginatedData()
      } else if (props.condition) {
        getData()
      }

      return
    }

    if (props.isPaginated) {
      getPaginatedData()
    } else {
      getData()
    }
  }, [])


  useEffect(() => {
    if (!isFirstGet) {
      getPaginatedData({
        ...props.params
      })
    }
  }, [page, rowsPerPage])

  useEffect(() => {
    if (data && props?.formActions?.setValue) {
      fillScreenData()
    }
  }, [data])

  return {
    data,
    error,
    refresh: props.isPaginated ? getPaginatedData : getData,
    isFirstGet,
    handleChangePage,
    handleChangeRowsPerPage,
    page,
    rowsPerPage,
    tableFooterControl: {
      typeFooter: props.isPaginated ? 'online' : 'offline',
      page,
      rowsPerPage,
      handleChangePage,
      handleChangeRowsPerPage
    }
  }
}

export default useGet
